import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, CreditCard, Building2, Copy, Upload, X, Wallet } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const PlansPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showBankTransfer, setShowBankTransfer] = useState(false);
  const [bankTransferPlan, setBankTransferPlan] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [depositorName, setDepositorName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [sessionPlan, setSessionPlan] = useState(null);

  const bankDetails = {
    accountName: "OMOYEMI ROMITI - JACOBS",
    accountNumber: "2155442551",
    bankName: "UBA BANK"
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/plans');
      setPlans(response.data);
    } catch (error) {
      toast.error('Failed to load plans');
    }
  };

  const handleStripePayment = async (planId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to subscribe');
      navigate('/login');
      return;
    }

    setLoading(true);
    setSelectedPlan(planId);
    setPaymentMethod('stripe');
    
    try {
      const originUrl = window.location.origin;
      const response = await api.post(`/checkout/session?plan_id=${planId}&origin_url=${originUrl}`);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setLoading(false);
      setSelectedPlan(null);
      setPaymentMethod(null);
    }
  };

  const handlePaystackPayment = async (plan) => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) {
      toast.error('Please login to subscribe');
      navigate('/login');
      return;
    }

    setLoading(true);
    setSelectedPlan(plan.id);
    setPaymentMethod('paystack');
    
    try {
      const user = JSON.parse(userData);
      const callbackUrl = `${window.location.origin}/payment/callback`;
      
      const payload = {
        plan_id: plan.id,
        email: user.email,
        callback_url: callbackUrl
      };
      
      // Include session_type for Per Session plan
      if (plan.has_sessions && selectedSession) {
        payload.session_type = selectedSession;
      }
      
      const response = await api.post('/paystack/initialize', payload);
      
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize Paystack payment');
      setLoading(false);
      setSelectedPlan(null);
      setPaymentMethod(null);
    }
  };

  const handleBankTransfer = (plan) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to subscribe');
      navigate('/login');
      return;
    }
    setBankTransferPlan(plan);
    setShowBankTransfer(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File too large. Max 5MB');
        return;
      }
      setProofFile(file);
    }
  };

  const handleSubmitBankTransfer = async () => {
    if (!depositorName.trim()) {
      toast.error('Please enter the depositor name');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('plan_id', bankTransferPlan.id);
      formData.append('depositor_name', depositorName);
      formData.append('amount', bankTransferPlan.price);
      if (proofFile) {
        formData.append('proof_file', proofFile);
      }
      // Include session_type for Per Session plan
      if (bankTransferPlan.has_sessions && selectedSession) {
        formData.append('session_type', selectedSession);
      }

      await api.post('/payments/bank-transfer', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Bank transfer submitted! We will verify your payment within 24 hours.');
      setShowBankTransfer(false);
      setBankTransferPlan(null);
      setProofFile(null);
      setDepositorName('');
      setSelectedSession(null);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit bank transfer');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-8 sm:py-12 md:py-20 px-4 sm:px-6 md:px-12 lg:px-20">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-black uppercase mb-4 tracking-tight" data-testid="plans-title">
            Choose Your Plan
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
            Select the membership that fits your fitness journey
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="bg-card/50 backdrop-blur-xl border border-white/10 rounded-xl p-4 sm:p-6 md:p-8 hover:border-primary/50 transition-all hover:scale-[1.02]"
              data-testid={`plan-card-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <h3 className="text-lg sm:text-xl md:text-2xl font-heading font-bold uppercase mb-2" data-testid={`plan-name-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}>
                {plan.name}
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">{plan.description}</p>
              
              <div className="mb-4 sm:mb-6">
                <span className="text-2xl sm:text-3xl md:text-4xl font-bold">₦{plan.price.toLocaleString()}</span>
                <span className="text-sm sm:text-base text-muted-foreground">
                  {plan.name === 'Per Session' ? '/session' : 
                   plan.name === 'Weekly Subscription' ? '/weekly' : 
                   plan.name === 'Monthly Subscription' ? '/month' : ''}
                </span>
              </div>
              
              <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <Check className="w-4 h-4 sm:w-5 sm:h-5 text-primary mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm sm:text-base text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <div className="space-y-2 sm:space-y-3">
                {/* Session Selection for Per Session Plan */}
                {plan.has_sessions && plan.sessions && (
                  <div className="mb-4 p-3 bg-primary/10 border border-primary/30 rounded-lg">
                    <p className="text-sm font-semibold mb-2 text-primary">Select Session:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {plan.sessions.map((session, idx) => (
                        <Button
                          key={idx}
                          type="button"
                          variant={selectedSession === session ? "default" : "outline"}
                          className={`text-xs sm:text-sm ${selectedSession === session ? 'bg-primary' : ''}`}
                          onClick={() => setSelectedSession(session)}
                          data-testid={`session-btn-${session.toLowerCase().replace(/[^a-z]/g, '-')}`}
                        >
                          {session}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                <Button
                  onClick={() => {
                    if (plan.has_sessions && !selectedSession) {
                      toast.error('Please select a session (Morning or Afternoon)');
                      return;
                    }
                    handlePaystackPayment(plan);
                  }}
                  disabled={loading && selectedPlan === plan.id}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider h-10 sm:h-12 text-sm sm:text-base"
                  data-testid={`paystack-btn-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Wallet className="w-4 h-4 mr-2" />
                  {loading && selectedPlan === plan.id && paymentMethod === 'paystack' ? 'Processing...' : 'Pay with Paystack'}
                </Button>
                
                <Button
                  onClick={() => {
                    if (plan.has_sessions && !selectedSession) {
                      toast.error('Please select a session (Morning or Afternoon)');
                      return;
                    }
                    handleStripePayment(plan.id);
                  }}
                  disabled={loading && selectedPlan === plan.id}
                  variant="outline"
                  className="w-full border-primary text-primary hover:bg-primary hover:text-white font-bold uppercase tracking-wider h-10 sm:h-12 text-sm sm:text-base"
                  data-testid={`stripe-btn-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {loading && selectedPlan === plan.id && paymentMethod === 'stripe' ? 'Processing...' : 'Pay with Card'}
                </Button>
                
                <Button
                  onClick={() => {
                    if (plan.has_sessions && !selectedSession) {
                      toast.error('Please select a session (Morning or Afternoon)');
                      return;
                    }
                    handleBankTransfer(plan);
                  }}
                  variant="outline"
                  className="w-full border-secondary text-secondary hover:bg-secondary hover:text-black font-bold uppercase tracking-wider h-10 sm:h-12 text-sm sm:text-base"
                  data-testid={`bank-transfer-btn-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Bank Transfer
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-8 sm:mt-12">
          <Link to="/" className="text-muted-foreground hover:text-primary transition-colors text-sm sm:text-base">
            ← Back to Home
          </Link>
        </div>
      </div>

      {/* Bank Transfer Dialog */}
      <Dialog open={showBankTransfer} onOpenChange={setShowBankTransfer}>
        <DialogContent className="w-[95vw] max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Building2 className="w-5 h-5 text-primary" />
              Bank Transfer Payment
            </DialogTitle>
            <DialogDescription className="text-sm sm:text-base">
              Transfer ₦{bankTransferPlan?.price?.toLocaleString()} for {bankTransferPlan?.name} plan
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 sm:space-y-6 py-4">
            {/* Bank Details Card */}
            <Card className="bg-primary/10 border-primary/30">
              <CardHeader className="pb-2 px-3 sm:px-6">
                <CardTitle className="text-sm font-medium text-primary">Bank Account Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 px-3 sm:px-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Account Name</p>
                    <p className="font-semibold text-sm sm:text-base">{bankDetails.accountName}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(bankDetails.accountName)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Account Number</p>
                    <p className="font-semibold text-lg sm:text-xl tracking-wider">{bankDetails.accountNumber}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(bankDetails.accountNumber)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Bank Name</p>
                    <p className="font-semibold text-sm sm:text-base">{bankDetails.bankName}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(bankDetails.bankName)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="pt-2 border-t border-primary/20">
                  <p className="text-xs text-muted-foreground">Amount to Pay</p>
                  <p className="font-bold text-xl sm:text-2xl text-primary">₦{bankTransferPlan?.price?.toLocaleString()}</p>
                </div>
              </CardContent>
            </Card>

            {/* Depositor Name */}
            <div>
              <Label htmlFor="depositor-name" className="text-sm sm:text-base">Depositor Name *</Label>
              <Input
                id="depositor-name"
                value={depositorName}
                onChange={(e) => setDepositorName(e.target.value)}
                placeholder="Name used for the transfer"
                className="mt-1 h-10 sm:h-12"
                data-testid="depositor-name-input"
              />
            </div>

            {/* Proof of Payment Upload */}
            <div>
              <Label className="text-sm sm:text-base">Proof of Payment (Optional)</Label>
              <div className="mt-1">
                {proofFile ? (
                  <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                    <span className="text-sm truncate flex-1 mr-2">{proofFile.name}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setProofFile(null)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-20 sm:h-24 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
                    <Upload className="w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground mb-2" />
                    <span className="text-xs sm:text-sm text-muted-foreground">Click to upload receipt</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*,.pdf"
                      onChange={handleFileChange}
                      data-testid="proof-upload-input"
                    />
                  </label>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Upload screenshot or PDF of your payment receipt (Max 5MB)
              </p>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
              <p className="text-xs sm:text-sm text-yellow-500">
                ⚠️ After making the transfer, click "Confirm Transfer" below. Your subscription will be activated within 24 hours after verification.
              </p>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowBankTransfer(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitBankTransfer}
              disabled={submitting || !depositorName.trim()}
              className="bg-primary w-full sm:w-auto"
              data-testid="confirm-transfer-btn"
            >
              {submitting ? 'Submitting...' : 'Confirm Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlansPage;
